import { useEffect, useCallback, useRef, useState } from 'react'
import websocketService from '../services/websocketService'
import { getUserData } from '../utility/Utils'

/**
 * Hook personalizado para gerenciar conexão WebSocket
 * @param {Object} options - Opções de configuração
 * @param {string} options.url - URL do servidor WebSocket
 * @param {boolean} options.autoConnect - Se deve conectar automaticamente
 * @param {Array} options.dependencies - Dependências para reconexão
 * @returns {Object} Estado e métodos do WebSocket
 */
export const useWebSocket = (options = {}) => {
  const {
    url = 'wss://wsbds.stg.theripen.com',
    autoConnect = true,
    dependencies = []
  } = options

  // Estados
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const [notifications, setNotifications] = useState([])

  // Refs para evitar re-renders desnecessários
  const listenersRef = useRef(new Map())
  const isInitializedRef = useRef(false)

  // Dados do usuário do Redux (se disponível)
  const userData = getUserData()

  /**
   * Conecta ao WebSocket
   */
  const connect = useCallback(() => {
    try {
      websocketService.connect(url)
      setConnectionError(null)
    } catch (error) {
      console.error('Erro ao conectar WebSocket:', error)
      setConnectionError(error.message)
    }
  }, [url])

  /**
   * Desconecta do WebSocket
   */
  const disconnect = useCallback(() => {
    websocketService.disconnect()
    setIsConnected(false)
  }, [])

  /**
   * Entra em uma sala de caso específico
   */
  const joinCaseRoom = useCallback((caseId) => {
    websocketService.joinCaseRoom(caseId)
  }, [])

  /**
   * Emite um evento para o servidor
   */
  const emit = useCallback((event, data) => {
    websocketService.emit(event, data)
  }, [])

  /**
   * Registra um listener para um evento
   */
  const on = useCallback((event, callback) => {
    // Armazena o callback na ref para cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, [])
    }
    listenersRef.current.get(event).push(callback)

    // Registra no serviço
    websocketService.on(event, callback)

    // Retorna função de cleanup
    return () => {
      websocketService.off(event, callback)
      const listeners = listenersRef.current.get(event)
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }
  }, [])

  /**
   * Remove um listener de um evento
   */
  const off = useCallback((event, callback) => {
    websocketService.off(event, callback)
    
    const listeners = listenersRef.current.get(event)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  /**
   * Manipula notificações recebidas
   */
  const handleNotification = useCallback((notification) => {
    console.log('🔔 Nova notificação recebida no hook:', notification)
    
    // Adiciona timestamp se não existir
    const notificationWithTimestamp = {
      ...notification,
      timestamp: notification.timestamp || new Date().toISOString(),
      id: notification.id || `notification-${Date.now()}-${Math.random()}`
    }

    console.log('📝 Notificação processada:', notificationWithTimestamp)

    // Atualiza o estado das notificações
    setNotifications(prev => {
      const newNotifications = [notificationWithTimestamp, ...prev]
      console.log('📊 Estado das notificações atualizado:', newNotifications.length, 'notificações')
      return newNotifications
    })

    // Aqui você pode adicionar lógica adicional como:
    // - Mostrar toast notification
    // - Reproduzir som
    // - Atualizar badge de notificações
    // - Etc.
  }, [])

  /**
   * Limpa uma notificação específica
   */
  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId))
  }, [])

  /**
   * Limpa todas as notificações
   */
  const clearAllNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  /**
   * Verifica o status da conexão
   */
  const checkConnection = useCallback(() => {
    const connected = websocketService.isSocketConnected()
    setIsConnected(connected)
    return connected
  }, [])

  // Efeito para inicialização e cleanup
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true

      // Registra listener para notificações
      websocketService.on('notification', handleNotification)

      // Conecta automaticamente se habilitado e há dados do usuário
      if (autoConnect && userData) {
        connect()
      }
    }

    // Cleanup na desmontagem
    return () => {
      if (isInitializedRef.current) {
        // Remove todos os listeners registrados
        listenersRef.current.forEach((listeners, event) => {
          listeners.forEach(callback => {
            websocketService.off(event, callback)
          })
        })
        listenersRef.current.clear()

        // Remove listener de notificações
        websocketService.off('notification', handleNotification)

        // Desconecta se necessário
        if (websocketService.isSocketConnected()) {
          disconnect()
        }
      }
    }
  }, []) // Removendo dependências para evitar reconexões desnecessárias

  // Efeito para reconexão baseada em dependências
  useEffect(() => {
    if (isInitializedRef.current && userData && dependencies.length > 0) {
      // Reconecta quando as dependências mudam
      if (websocketService.isSocketConnected()) {
        disconnect()
      }
      
      setTimeout(() => {
        connect()
      }, 1000) // Pequeno delay para evitar conflitos
    }
    // eslint-disable-next-line
  }, dependencies)

  // Verifica conexão periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      checkConnection()
    }, 5000) // Verifica a cada 5 segundos

    return () => clearInterval(interval)
  }, [checkConnection])

  return {
    // Estados
    isConnected,
    connectionError,
    notifications,
    
    // Métodos de conexão
    connect,
    disconnect,
    checkConnection,
    
    // Métodos de eventos
    on,
    off,
    emit,
    
    // Métodos de salas
    joinCaseRoom,
    
    // Métodos de notificações
    clearNotification,
    clearAllNotifications,
    
    // Utilitários
    isSocketConnected: websocketService.isSocketConnected()
  }
}

export default useWebSocket
